import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useCallback, useMemo } from 'react';
import usePcConsoleTimelineChart from '../../hooks/usePcConsoleTimelineChart';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { getChartsShowCount } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import {
  eChartPercentageYAxis,
  percentageFormat,
  reviewsFormat,
} from '@/pages/opinion/utils/value';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { getPositiveNegativeIconName } from '@op/pages/Performance/components/MentionsSentiment/chart';

const seriesDataKeys: any = [
  {
    text: 'Added Reviews Positive%',
    key: 'select_recommend_score',
    type: 'line',
    color: '#4DAB7D',
    valueKey: 'score',
    sort: 1,
    showLabel: true,
    decimalDigit: 1
  },
  {
    text: 'Positive',
    key: 'select_recommend',
    type: 'bar',
    color: '#69D19C',
    valueKey: 'review',
    sort: 2,
  },
  {
    text: 'Negative',
    key: 'select_non_recommend',
    type: 'bar',
    color: '#F17C86',
    valueKey: 'review',
    lessThanZero: true,
    sort: 3,
  },
];
export default function SteamScoreTrendBox({
  filterParams,
  platformData,
  loading,
  negativeLessThanZero = false,
  height = 260,
  otherKeyMetrics,
  onChartClick,
}: {
  filterParams?: any;
  platformData?: any;
  loading?: boolean;
  negativeLessThanZero?: boolean;
  height?: number;
  otherKeyMetrics?: any[];
  onChartClick?: (data: { time: string; sentiment?: 'positive' | 'negative' }) => void;
}) {
  const { chartMap } = usePcConsoleTimelineChart({
    platformData,
    filterParams,
  });

  const { formatMessage } = useIntl();
  const [options, noData] = useMemo(() => {
    const legends: { name: string; itemStyle: { color: string } }[] = [];
    const series: { name: string; type: string; data: any[] }[] = [];
    let xAxis: string[] = [];
    let totalList: Array<number> = [];
    const minTotalList: Array<number> = [];
    let nodata = true;
    const list = [...seriesDataKeys];
    if (otherKeyMetrics) {
      list.push(...otherKeyMetrics);
      list.sort((a, b) => (a.sort || 0) - (b.sort || 0));
    }
    list.map(
      ({
        text,
        key,
        type,
        color,
        valueKey,
        lessThanZero,
        lineStyle,
        showLabel,
        decimalDigit = 2,
      }) => {
        const data = chartMap[key];
        if (data) {
          if (nodata) {
            nodata = data.every((item) => {
              const numericValues = Object.values(item).filter((v) => typeof v === 'number');
              return numericValues.length > 0 && numericValues.every((v) => v === 0);
            });
          }
          const needShowLabel = (showLabel && data.length <= 24) || false;
          const seriesObj: any = {
            name: text,
            type,
            barMaxWidth: 80,
            data: data.map((item) => {
              const value = item[valueKey];
              if (value === null) return null;
              const showValue =
                negativeLessThanZero && lessThanZero ? 0 - value : value;

              if (needShowLabel) {
                return {
                  value: showValue,
                  itemStyle: {
                    opacity: 1,
                  },
                };
              }

              return showValue;
            }),
            itemStyle: { color },
            label: {
              show: needShowLabel,
              opacity: 1,
              formatter: ({ value }: { value: number }) => {
                if (value === null) return '';
                if (type === 'bar') {
                  return reviewsFormat(Math.abs(value));
                }
                return percentageFormat(value * 100, '0%', decimalDigit);
              },
            },
          };

          if (lineStyle) {
            seriesObj.lineStyle = lineStyle;
          }

          if (type === 'bar') {
            seriesObj.stack = 'total';
            totalList = data.map((item: number, index: number) => {
              const total = totalList[index] || 0;
              return total + Math.abs(item || 0);
            });
          } else {
            minTotalList.push(...seriesObj.data);
            seriesObj.yAxisIndex = 1;
          }
          series.push(seriesObj);
          legends.push({
            name: text,
            itemStyle: { color },
          });

          if (xAxis.length === 0 && data) {
            xAxis = data.map((item) => item.time);
          }
        }
      },
    );

    const currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 1.2),
        appendToBody: false,
        confine: false,
        formatter: (params: any) => {
          let title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filterParams.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title: title,
            beforeSort: false,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return getPositiveNegativeIconName(name, formatMessage);
            },
            valueFormat: (value, ele) => {
              if (ele.seriesType === 'bar') {
                return reviewsFormat(Math.abs(value));
              }
              const item = list.find((item) => item.text === ele.seriesName);
              return percentageFormat(
                value * 100,
                '-',
                item?.decimalDigit ?? 2,
              );
            },
            dealwithRecords(records) {
              const valueList = records.filter((item) => {
                return !(
                  item.value.includes('%') || isNaN(Number(item.originValue))
                );
              });
              const total = valueList.reduce(
                (pre, cur) => pre + (Math.abs(cur?.originValue) || 0),
                0,
              );
              records.unshift({
                text: formatMessage({ id: 'Mentions' }),
                value: reviewsFormat(total),
                originValue: total,
                originText: 'Mentions',
              });
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 25,
        left: 20,
        right: 20,
        containLabel: true,
      },
      legend: {
        data: legends,
        textStyle: {
          rich: {
            a: {
              color: '#888888',
              fontSize: 12,
            },
            space: {
              width: 2,
            },
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamPositive,
              },
            },
            negative: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamNegative,
              },
            },
          },
        },
        formatter: (name: string) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (name === 'Negative') {
            return `{negative|} ${newName}`;
          }
          return `{positive|} ${newName}`;
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              const data = getChartsShowCount(Math.abs(value), 1);
              return data;
            },
          },
        },
        {
          type: 'value',
          max: 1,
          min: 0,
          axisLabel: {
            formatter: (value: any) => {
              return eChartPercentageYAxis(value);
            },
          },
        },
      ],
      series: series,
    };
    return [currentOptions, series.length === 0 || nodata];
  }, [chartMap, formatMessage, filterParams, negativeLessThanZero]);

  const handleClick = useCallback(
    (params: any) => {
      if (!onChartClick || !params?.name) return;
      const data: { time: string; sentiment?: 'positive' | 'negative' } = {
        time: params.name,
      };
      if (params.seriesName === 'Positive') {
        data.sentiment = 'positive';
      } else if (params.seriesName === 'Negative') {
        data.sentiment = 'negative';
      }
      onChartClick(data);
    },
    [onChartClick],
  );

  return (
    <EChartCommon
      options={options}
      height={height}
      loading={loading}
      empty={noData}
      optionsProps={{ type: 'bar' }}
      leftTitle={formatMessage({ id: 'Mentions' })}
      rightTitle={
        <>
          <img src={steamPositive}></img>
          {formatMessage({ id: 'Positive%' })}
        </>
      }
      eventsDict={onChartClick ? { click: handleClick } : undefined}
    ></EChartCommon>
  );
}
